import { webappPortalOrders } from "./webapp-portal-orders";
import { webappPortalAfterSales } from "./webapp-portal-aftersales";
/**
 * Web app portal (SD-S1-57): `/chat/<slug>/tickets`, the magic-link sign-in and the e-mailed rating page. Words follow
 * `mockups/tickets/webapp-portal.html` (frames P1 and P5) and the widget's My tickets copy; what the server itself says (status
 * labels, why an action is refused) is shown as sent.
 */
export const webappPortal = {
  orders: webappPortalOrders,
  afterSales: webappPortalAfterSales,
  pageTitle: "{{org}} support portal",
  auth: { footer: "Your tickets stay between you and the team that helps you." },
  nav: { label: "Portal", tickets: "My tickets", ticketsShort: "My tickets", chat: "Chat with the assistant", chatShort: "Chat" },
  user: { menu: "Account menu", fallbackName: "Signed in", member: "Member", orgAdmin: "Organisation admin" },
  data: {
    menu: "Your data",
    back: "Back to my tickets",
    title: "Your data",
    intro: "Under the Data Privacy Act (RA 10173) you can get a copy of what {{org}} holds about you here, or ask for it to be erased.",
    consentTitle: "Your consent",
    consentGiven: "You accepted privacy notice {{version}} on {{date}}.",
    consentNone: "No consent is on file for you yet.",
    consentUnknown: "Your consent on file is shown once your data is prepared below.",
    exportTitle: "Get a copy of your data",
    exportBody: "We prepare one file with your details, your consent, the public messages of your tickets and, where you have them, your orders, quotes, equipment, visits and returns. The team's internal notes are not part of it.",
    exportButton: "Prepare and download my data",
    exportAgain: "Download again",
    exportPreparing: "Preparing your data",
    exportReady: "Your data is ready and was saved to your device. Tickets included: {{count}}.",
    exportSections: "Also in your file",
    exportSection: { orders: "Orders", quotes: "Quotes", carts: "Open carts", assets: "Equipment you reported", visits: "Service visits", installations: "Installation acceptances", rmas: "Returns" },
    exportFailed: "We could not prepare your data. Please try again.",
    downloaded: "Your data was downloaded",
    rateLimited: "You have tried this a few times. Wait a moment, then try again.",
    eraseTitle: "Erase my data",
    eraseBody: "Ask {{org}} to erase your personal details. You stay unnamed on your tickets and orders, but the tickets, orders and other records themselves are kept.",
    eraseButton: "Erase my data",
    eraseFailed: "We could not erase your data. Please try again.",
    confirmTitle: "Erase your data?",
    confirmIntro: "This permanently erases, for {{org}}:",
    confirmContact: "your name, e-mail address, phone number and job title",
    confirmWords: "the personal details in what you wrote on your tickets, and the files you sent",
    confirmRecords: "your delivery addresses, sign-off names and signatures on orders, quotes, visits, acceptances and returns, and your open carts",
    confirmKeep: "{{org}} keeps the tickets, orders, visits and other records themselves, without your name. This cannot be undone and you will be signed out.",
    confirmCancel: "Keep my data",
    confirmErase: "Erase my data",
    erasedTitle: "Your data was erased",
    erasedBody: "Your personal details were removed. You will be signed out of the portal now.",
    erasedDone: "Sign out",
  },
  index: { title: "Choose a ticket", hint: "Pick one from the list to read it and reply, or report a new problem." },
  list: {
    title: "My tickets",
    new: "New ticket",
    search: "Search by number or title",
    clearSearch: "Clear search",
    noMatches: "No matches",
    noMatchesHint: "Try a different number or word.",
    emptyTitle: "No tickets yet",
    emptyHint: "When you report a problem, you can follow it here.",
    emptyCta: "Report a problem",
    closedEmptyTitle: "No closed tickets",
    closedEmptyHint: "Tickets that are resolved and closed are kept here.",
    orgEmptyTitle: "No tickets from your organisation yet",
    orgLocked: "Colleagues’ tickets appear here when they share them, or when your organisation admin lets you see them.",
    listLabel: "Tickets",
    tabsLabel: "Ticket status",
    scopeLabel: "Whose tickets",
    scope: { mine: "Mine", organisation: "My organisation" },
    tab: { open: "Open", closed: "Closed" },
    showMore: "Show more",
    unread: "Unread update",
    actionNeeded: "Action needed",
  },
  detail: {
    back: "Back to my tickets",
    created: "Created {{when}}",
    assignee: "Handled by {{name}}",
    followUpOf: "Follow-up of {{number}}",
    told: "What you told us",
    description: "Description",
    timeline: "Timeline",
    files: "Attachments",
    liveOn: "Live",
    polling: "Updating",
    notFoundTitle: "We could not find that ticket",
    notFoundBody: "It may belong to someone else, or the number is wrong. Check the e-mail it came from.",
  },
  priority: { low: "Low", normal: "Normal", high: "High", urgent: "Urgent" },
  thread: {
    label: "Ticket conversation",
    empty: "No messages yet. We will update you here.",
    you: "You",
    colleague: "{{name}} (colleague)",
    team: "Our team",
    assistant: "Assistant",
    removed: "This message was removed.",
    openFile: "Open {{name}}",
    fileFailed: "We could not open that file. Please try again.",
  },
  composer: {
    label: "Reply",
    placeholder: "Write your message…",
    send: "Send",
    sent: "Reply sent",
    writeFirst: "Write a message before sending.",
    waitUpload: "Wait for the upload to finish before sending.",
    closedNote: "This ticket is closed. If you still need help, start a new ticket.",
    cannotReply: "You can read this ticket, but only the person who opened it can reply.",
    followUp: "We opened {{number}} for your message, because this ticket is closed.",
  },
  files: {
    attach: "Attach",
    remove: "Remove {{name}}",
    uploading: "Uploading…",
    scanning: "Scanning…",
    clean: "Scanned, clean",
    blocked: "We could not accept this file.",
    failed: "Upload failed. Try again.",
    type: "That type of file is not accepted.",
    size: "That file is too large.",
    count: "Up to {{n}} files on one message.",
  },
  resolved: {
    title: "Resolved — please confirm",
    closedTitle: "This ticket is closed",
    confirm: "Confirm resolved",
    confirmed: "Confirmed. This ticket is closed.",
    stillProblem: "Still a problem",
    stillHint: "Tell us what is still wrong with {{number}} and we will reopen it.",
    stillLabel: "What is still wrong?",
    stillShort: "Tell us what is still wrong (at least {{n}} characters).",
    stillSend: "Reopen ticket",
    reopened: "Ticket reopened.",
  },
  csat: {
    question: "How satisfied are you with how we handled {{number}}?",
    stars: "Rating",
    starLabel: "{{n}} of 5 stars",
    why: "What went wrong?",
    comment: "Comment (optional)",
    send: "Send rating",
    thanks: "Thank you! Your {{n}}-star rating was recorded.",
    rated: "You rated this {{n}} stars",
  },
  csatPage: {
    title: "How did {{org}} do?",
    score: "{{n}} of 5 stars",
    send: "Send my rating",
    note: "Your rating goes to the team that handled your ticket.",
    thanksTitle: "Thank you for your rating",
    thanksBody: "Your {{n}}-star rating for {{number}} was recorded.",
    replaced: "It replaces the rating you gave earlier for this ticket.",
    usedTitle: "This rating link was already used",
    usedBody: "Thank you, your answer is recorded. A link works once.",
    expiredTitle: "This rating link has expired",
    expiredBody: "Rating links last a limited time. You can still rate the ticket from your tickets page.",
    invalidTitle: "This rating link is not valid",
    invalidBody: "It may have been cut short when it was copied. Open the link from the e-mail again.",
    unavailableTitle: "Rating is not available here",
    unavailableBody: "This service desk is not taking ratings right now.",
    viewTickets: "Go to my tickets",
  },
  actions: {
    more: "More actions",
    downloadPdf: "Download as PDF",
    downloadFailed: "The PDF could not be downloaded. Please try again.",
    receiptsNote: "The team can see when you open their replies.",
    share: "Share with my organisation",
    unshare: "Stop sharing with my organisation",
    shareOn: "Shared with your organisation",
    shareOff: "Not shared with your organisation",
    addColleague: "Add a colleague",
    colleagueHint: "A colleague you add can follow {{number}} and reply to it.",
    colleagueLabel: "Colleague",
    colleaguePlaceholder: "Choose a colleague",
    colleagueRequired: "Choose a colleague first.",
    colleagueAdd: "Add colleague",
    colleagueAdded: "Colleague added",
    withdraw: "Withdraw this ticket",
    withdrawHint: "Withdraw {{number}} if you no longer need help. The team is told, and it is closed.",
    withdrawReason: "Why are you withdrawing it? (optional)",
    withdrawConfirm: "Withdraw ticket",
    withdrawn: "Ticket withdrawn",
  },
  emailAction: {
    confirmTitle: "Confirm that this is resolved",
    confirmBody: "If everything works now, confirm it and we will close the ticket.",
    stillTitle: "Tell us what is still wrong",
    stillBody: "We will reopen the ticket and the team will pick it up again.",
    withdrawTitle: "Withdraw this ticket",
    withdrawBody: "If you no longer need help, withdraw it and the team will stop working on it.",
    unavailableTitle: "{{number}} cannot be changed this way",
    unavailableBody: "It is now “{{status}}”. Open the ticket to see what you can do.",
    view: "Open the ticket",
    notNow: "Not now",
  },
  errors: {
    title: "We could not load this",
    generic: "Something went wrong on our side. Please try again.",
    offline: "You seem to be offline. Check your connection and try again.",
    limited: "Too many requests just now. Please wait a little and try again.",
    action: "That didn't go through. Please try again.",
    unavailableTitle: "Tickets are not available here right now",
    unavailableBody: "This service desk is not open for tickets yet. Please use the chat to reach the team.",
  },
  new: {
    title: "New ticket",
    back: "Back",
    step: "Step {{a}} of {{b}}",
    groupTitle: "What do you need?",
    groupHint: "Pick a group, then the type.",
    typeCount: "Types: {{n}}",
    chooseType: "Choose a type",
    otherGroup: "Other",
    type: "Type",
    fieldTitle: "Title",
    fieldDescription: "Description",
    photos: "Photos or video",
    optional: "optional",
    required: "required",
    chooseOne: "Choose one",
    yes: "Yes",
    no: "No",
    continue: "Continue",
    review: "Review",
    filesCount: "Attachments: {{n}}",
    backToEdit: "Back to edit",
    submit: "Submit ticket",
    fixFields: "Fix the highlighted fields.",
    created: "Ticket {{number}} created",
    createdHint: "We will e-mail you when there is an update. You can follow it here too.",
    view: "View ticket",
    loadFailed: "We could not load the ticket types",
    unavailableTitle: "New tickets are not available here yet",
    unavailableBody: "Describe the problem in chat and we will open a ticket for you.",
    describeInChat: "Describe it in chat instead",
    issue: {
      required: "This is required.",
      tooLong: "That is too long.",
      email: "Enter a valid e-mail address.",
      number: "Enter a number.",
      integer: "Enter a whole number.",
      date: "Enter a valid date.",
      option: "Choose one of the options.",
      title: "Keep the title to {{max}} characters or fewer.",
      description: "Keep the description to {{max}} characters or fewer.",
    },
  },
  handoff: {
    working: "Opening your tickets",
    failedTitle: "We could not open your tickets",
    failedBody: "The link from the chat works once and for a short time. Sign in with your e-mail to continue.",
    failedToast: "That link could not be used. Sign in to continue.",
    signIn: "Sign in",
  },
  signIn: {
    requestTitle: "Sign in to see your tickets",
    requestBody: "Enter the e-mail {{org}} knows you by. We will send you a link, no password needed.",
    emailLabel: "E-mail",
    emailPlaceholder: "you@company.com",
    emailInvalid: "Enter a valid e-mail address.",
    send: "E-mail me a sign-in link",
    noPassword: "No password. Never share this link.",
    requestLimited: "Too many tries. Please wait a little before asking again.",
    requestFailed: "We could not send the link. Please try again.",
    requestUnavailable: "Sign-in by e-mail is not available here right now.",
    captcha: "Please wait for the security check to finish, then try again.",
    sentTitle: "Check your inbox",
    sentBody: "If this address is on file, a sign-in link is on its way to {{email}}. It works once and expires in {{minutes}} minutes.",
    sentDevice: "Open the link on this device: it only works in the browser you asked from.",
    resend: "Send the link again",
    resendIn: "Send again in {{time}}",
    resentToast: "A new sign-in link was sent",
    change: "Use a different e-mail",
    endedIdle: "You were signed out after 30 minutes without activity. Sign in again to continue.",
    endedAbsolute: "For your security, a session ends after 12 hours. Sign in again to continue.",
    endedExpired: "Your session has ended. Sign in again to continue.",
    endedSignedOut: "You are signed out.",
    linkTitle: "Sign in to {{org}}",
    linkBody: "Continue to your tickets. This link works once and expires in {{minutes}} minutes.",
    deviceNote: "It is tied to this device: {{device}}.",
    wasSentTo: "The link was sent to {{email}}.",
    deviceBoth: "{{browser}} on {{system}}",
    continue: "Continue",
    sendNew: "Send a new link",
    usedTitle: "This link was already used",
    expiredTitle: "This link has expired",
    usedBody: "Links work once and last {{minutes}} minutes. We can send you a new one.",
    mismatchTitle: "This link was asked for on another device",
    mismatchBody: "For your safety a link only works in the browser it was asked from. Send a new link from this device.",
    invalidTitle: "This link is not valid",
    invalidBody: "It may have been cut short when it was copied. Send a new link.",
    unavailableTitle: "Sign-in is not available here",
    unavailableBody: "{{org}} is not taking sign-ins right now. Please try again later.",
    limitedTitle: "Too many tries",
    limitedBody: "Please wait a little and try again.",
    offlineTitle: "You seem to be offline",
    offlineBody: "Check your connection and try again. The link has not been used up.",
    failedTitle: "We could not sign you in",
    failedBody: "Something went wrong on our side. Try again, or send a new link.",
  },
} as const;
